import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, Globe, FileText, Shield, TrendingUp, Zap, BarChart2 } from 'lucide-react';

const GlobalTrade = () => {
  const useCases = [
    {
      icon: FileText,
      title: 'Trade Document Automation',
      description: 'AI that extracts, validates, and routes Bills of Lading, Letters of Credit, Certificates of Origin, and customs declarations — end-to-end.',
      outcomes: ['90% reduction in manual document processing', 'Sub-second validation against trade rules', 'Seamless customs system integration'],
    },
    {
      icon: Globe,
      title: 'HSN / HS Code Classification',
      description: 'ML models that automatically classify goods to the correct HS code, reducing customs delays and duty miscalculation risk.',
      outcomes: ['99%+ classification accuracy', 'Multi-jurisdiction support', 'Real-time tariff rate lookup'],
    },
    {
      icon: Shield,
      title: 'Trade Compliance & Sanctions Screening',
      description: 'Automated screening of counterparties, goods, and routes against sanctions lists, export controls, and denied-party databases.',
      outcomes: ['Real-time sanctions screening', 'Automated red-flag escalation', 'Full compliance audit trail'],
    },
    {
      icon: TrendingUp,
      title: 'Trade Finance Risk Intelligence',
      description: 'Predictive risk models for counterparty creditworthiness, country risk, and payment default — built on structured and unstructured data.',
      outcomes: ['Improved credit decisioning', 'Country risk scoring', 'Fraud pattern detection'],
    },
    {
      icon: Zap,
      title: 'Logistics & Freight Optimisation',
      description: 'AI-driven routing, carrier selection, and freight rate benchmarking that cuts costs and improves on-time delivery performance.',
      outcomes: ['10–20% freight cost reduction', 'Predictive ETAs', 'Automated carrier tender and award'],
    },
    {
      icon: BarChart2,
      title: 'Market & Tariff Intelligence',
      description: 'Natural language dashboards that surface tariff changes, free trade agreement updates, and market access windows relevant to your trade flows.',
      outcomes: ['Real-time trade policy monitoring', 'FTA utilisation analysis', 'Automated duty drawback identification'],
    },
  ];

  const caseStudies = [
    {
      title: 'HSN Code Classification Automation',
      result: 'Automated HS classification for 200,000+ SKUs across 40 product categories, cutting clearance delays by 70%.',
      link: '/case-studies/hsn-classification',
    },
    {
      title: 'Trade Document Intelligence Platform',
      result: 'Reduced trade document processing time from 4 hours to 8 minutes per shipment for a global commodity trader.',
      link: '/case-studies/trading-automation',
    },
  ];

  const techStack = [
    'Azure Document Intelligence', 'GPT-4 for document parsing', 'Python / spaCy / Hugging Face',
    'World Customs Organization APIs', 'Sanctions list integrations (OFAC, UN, EU)',
    'Databricks for trade data pipelines', 'Power BI trade dashboards', 'SAP GTS / Oracle GTM connectors',
  ];

  return (
    <>
      <Helmet>
        <title>AI for Global Trade | Trade Document Automation, Compliance & Logistics AI | Agility</title>
        <meta
          name="description"
          content="AI solutions for global trade companies. Trade document automation, HS code classification, sanctions screening, trade finance risk intelligence, and freight optimisation. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="global trade AI, trade document automation, HS code classification AI, trade compliance AI, sanctions screening, trade finance AI, customs automation"
        />
        <link rel="canonical" href="https://agility.ai/industries/global-trade" />
        <meta property="og:title" content="AI for Global Trade | Agility" />
        <meta property="og:description" content="AI solutions for global trade: document automation, compliance, and logistics intelligence." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">Global Trade</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We build AI systems that automate trade documents, enforce compliance, and unlock intelligence across the full international trade lifecycle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to a Global Trade AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Use Cases for Global Trade</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From document processing to compliance screening — AI across every stage of international commerce.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Global Trade Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Modernise your global trade operations with AI</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Production-ready trade AI in 3–8 weeks. Let's start with your highest-friction workflow.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default GlobalTrade;
